import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/react';
import React from 'react';

import { Paragraph } from 'DesignLibrary/atoms';
import Text from 'Shared/Text';

export interface SessionExpiredModalProps {
  isOpen: boolean;
}

/**
 * Shown when the AAPI session pulse returns a 401, meaning the user's session has expired
 * (idle timeout, or ALEX was opened in another window).
 *
 * This is a terminal state: the pulse loop has already stopped and the only way forward is to
 * go back to the benefits enrollment site and relaunch ALEX, so the modal is intentionally
 * non-dismissible — there is no close button and overlay/esc clicks do nothing.
 *
 * Styling mirrors the BC (Parabola) session-expired popup: a light, semi-transparent white
 * backdrop (rgba(255,255,255,0.8)) that fades/blurs the page behind it, and a large centered
 * card with a 4px radius. Copy is sourced from Contentful (call_to_action.session_expired_modal)
 * so it can be localized.
 */
export const SessionExpiredModal = ({ isOpen }: SessionExpiredModalProps) => (
  <Modal
    isOpen={isOpen}
    // Terminal state — nothing to close to. Provide a no-op so Chakra is happy.
    onClose={() => {}}
    isCentered
    closeOnOverlayClick={false}
    closeOnEsc={false}
    motionPreset="scale"
  >
    <ModalOverlay bg="rgba(255, 255, 255, 0.8)" backdropFilter="blur(2px)" />
    <ModalContent
      maxW="calc(100vw - 32px)"
      w="fit-content"
      borderRadius="4px"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="lg"
      data-testid="session-expired-modal"
    >
      <ModalBody display="flex" flexDirection="column" gap={4} p={10} overflowX="auto">
        <Paragraph weight="bold" style={{ whiteSpace: 'nowrap' }}>
          <Text field="call_to_action.session_expired_modal.expired_message" />
        </Paragraph>
        <Paragraph style={{ whiteSpace: 'nowrap' }}>
          <Text field="call_to_action.session_expired_modal.resume_message" />
        </Paragraph>
        <Paragraph style={{ whiteSpace: 'nowrap' }}>
          <Text field="call_to_action.session_expired_modal.restart_message" />
        </Paragraph>
      </ModalBody>
    </ModalContent>
  </Modal>
);

export default SessionExpiredModal;
